import axios from '@/libs/axios';
import HelperService from '@/services/HelperService';

const helperService = new HelperService();

const BASE_URL = '/api/v1/prisoner-verification-records';

export default {
  /**
   * Fetches a list of resident verification records with pagination and filtering.
   * @param {object} params - Query parameters for pagination and filtering.
   * @param {number} [params.skip=0] - Number of records to skip.
   * @param {number} [params.limit=10] - Maximum number of records to return.
   * @param {string} [params.prisonId] - Optional: Filter by prison ID.
   * @param {string} [params.status='AwaitingVerification'] - Optional: Filter by verification status.
   * @returns {Promise<object>} - A promise that resolves to a paginated response (data, count).
   */
  async getVerificationRecordsPaginated(params = {
    skip: 0, limit: 10, status: '', prisonerNumber: '',
  }) {
    try {
      const queryString = helperService.buildSearchQueryString(params);
      const response = await axios.get(`${BASE_URL}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching paginated verification records:', error);
      throw error;
    }
  },

  /**
   * Retrieves a single resident verification record by its unique ID.
   * @param {string} recordId - The unique ID of the resident verification record.
   * @returns {Promise<object>} - A promise that resolves to a single PrisonerVerificationRecord object.
   */
  async getSingleVerificationRecord(recordId) {
    try {
      const response = await axios.get(`${BASE_URL}/${recordId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching single verification record with ID ${recordId}:`, error);
      throw error;
    }
  },

  /**
   * Approves a resident verification record.
   * @param {string} recordId - The unique ID of the resident verification record to approve.
   * @returns {Promise<object>} - A promise that resolves to the updated PrisonerVerificationRecord object.
   */
  async approveVerificationRecord(recordId) {
    try {
      const response = await axios.post(`${BASE_URL}/${recordId}/approve`);
      return response.data;
    } catch (error) {
      console.error(`Error approving verification record with ID ${recordId}:`, error);
      throw error;
    }
  },

  /**
   * Rejects a resident verification record with a given reason.
   * @param {string} recordId - The unique ID of the resident verification record to reject.
   * @param {string} rejectionReason - The reason for rejecting the record.
   * @returns {Promise<object>} - A promise that resolves to the updated PrisonerVerificationRecord object.
   */
  async rejectVerificationRecord(recordId, rejectionReason) {
    try {
      const response = await axios.post(`${BASE_URL}/${recordId}/reject`, { rejection_reason: rejectionReason });
      return response.data;
    } catch (error) {
      console.error(`Error rejecting verification record with ID ${recordId}:`, error);
      throw error;
    }
  },

  /**
   * Retrieves a resident verification record by prisoner number and prison ID.
   * @param {string} prisonerNumber - The prisoner's number (URL param).
   * @param {string} prisonId - The prison ID (query param).
   * @returns {Promise<object>} - The resident verification record.
   */
  async getPrisonerVerificationRecordByDetails(prisonerNumber, prisonId) {
    try {
      const response = await axios.get(
        `${BASE_URL}/${prisonerNumber}/details`,
        {
          params: { prisonId },
        },
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching resident record for prisoner ${prisonerNumber} in prison ${prisonId}:`,
        error,
      );
      throw error;
    }
  },
};
